import { DEBOUNCE_TIME_MS, FETCH_MODE } from '@infield/constants/common';
import { useDebounce } from '@infield/hooks';
import { useSearchParams } from 'react-router-dom';

/**
 * React hook to manage a debounced search query via the URL.
 * @returns {{
 *   # the last set search query
 *   searchQuery: string,
 *   # after 500 milliseconds becomes the last set search query
 *   debouncedQuery: string,
 *   # set the search query
 *   setSearchQuery: (value: string) => void,
 *   fetchMode: 'LIST' | 'SEARCH'
 * }} An object containing the query string, debounced value, and fetch mode.
 */

export const useSearchQuery = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    search: '',
  });

  const searchQuery = searchParams.get('search') || '';
  const debouncedQuery = useDebounce(searchQuery, DEBOUNCE_TIME_MS);
  const fetchMode = debouncedQuery === '' ? FETCH_MODE.LIST : FETCH_MODE.SEARCH;
  const setSearchQuery = (value: string) => {
    if (value) {
      searchParams.set('search', value);
    } else {
      searchParams.delete('search');
    }
    setSearchParams(searchParams, { replace: true });
  };
  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    fetchMode,
  };
};
